import type { ActionOutcome } from "../ui/ActionConfirmation.js";
import type {
  ScheduleBlockApply,
  ScheduleBlockConflict,
  ScheduleBlockInput,
} from "./scheduleBlocks.js";
import type {
  BulkCancelCreditChoice,
  BulkCancelPreview,
  BulkCancelResult,
} from "./staff.js";
import { ApiError } from "../api.js";
import { formatDate, formatTime } from "../format.js";
import type { CancellationChoices } from "./cancellationChoices.js";

/** Match the schedule API's whitespace normalization. */
export function normalizeTimeOffReason(value: string): string {
  return value.trim().replace(/\s+/g, " ");
}

/** Frozen recovery request: replay always uses these exact values and identity. */
export interface TimeOffCancellationOperation {
  key: string;
  appointmentIds: number[];
  reason: string;
  notify: boolean;
  creditResolutions: BulkCancelCreditChoice[];
  preview: BulkCancelPreview;
  choices: CancellationChoices;
  applied: ScheduleBlockApply;
}

export function confirmsTimeOff(
  applied: ScheduleBlockApply,
  providerId: number,
  input: ScheduleBlockInput,
): boolean {
  const block = applied?.block;
  const time = (value: string | null | undefined) => value?.slice(0, 5) ?? null;
  return (
    block?.status === "active" &&
    block.provider_id === providerId &&
    block.block_date === input.block_date &&
    block.end_date === (input.end_date || input.block_date) &&
    time(block.start_time) === time(input.start_time) &&
    time(block.end_time) === time(input.end_time) &&
    normalizeTimeOffReason(block.reason) ===
      normalizeTimeOffReason(input.reason)
  );
}

export function confirmsCancellationPreview(
  preview: BulkCancelPreview,
  ids: number[],
): boolean {
  return (
    preview?.requested_count === ids.length &&
    Array.isArray(preview.items) &&
    preview.items.length === ids.length &&
    new Set(preview.items.map((item) => item.appointment_id)).size ===
      ids.length &&
    preview.items.every((item) => ids.includes(item.appointment_id))
  );
}

/** Only a known pre-write policy refusal permits changing the frozen request. */
export function cancellationPreviewChanged(cause: unknown): boolean {
  return (
    cause instanceof ApiError &&
    cause.status === 409 &&
    (cause.code === "stale_preview" ||
      /^(Choose (Return or Keep|Issue credit or Keep payment|what happens to the credit or payment)|Issue or keep payment only applies|Return or Keep only applies|Keep credit used is only available|Keep payment is only available)/i.test(
        cause.message,
      ) ||
      /appointment state changed|preview (?:became |is )?stale|changed since (?:the )?preview/i.test(
        cause.message,
      ))
  );
}

export function conflictDescription(conflict: ScheduleBlockConflict): string {
  return `${formatDate(conflict.starts_at)} · ${formatTime(conflict.starts_at)}–${formatTime(conflict.ends_at)} — ${conflict.customer_names.join(", ") || "Booked lesson"} (#${conflict.appointment_id})`;
}

/** A well-formed 200 is the backend's stored, final receipt for this key. */
export function confirmsCancellationResult(
  result: BulkCancelResult,
  ids: number[],
): boolean {
  if (!result || !Array.isArray(result.results)) return false;
  const counts = [
    result.requested_count,
    result.cancelled_count,
    result.already_cancelled_count,
    result.failed_count,
    result.credits_restored,
    result.credits_granted ?? 0,
  ];
  return (
    counts.every((value) => Number.isSafeInteger(value) && value >= 0) &&
    result.requested_count === ids.length &&
    result.results.length === ids.length &&
    new Set(result.results.map((row) => row?.appointment_id)).size ===
      ids.length &&
    result.results.every(
      (row) =>
        row &&
        ids.includes(row.appointment_id) &&
        typeof row.success === "boolean" &&
        typeof row.credit_review_required === "boolean" &&
        (row.message === null || typeof row.message === "string") &&
        (row.final_status === null || typeof row.final_status === "string") &&
        Number.isSafeInteger(row.credits_restored) &&
        row.credits_restored >= 0 &&
        (!row.success ||
          ["cancelled", "canceled", "rejected"].includes(
            row.final_status?.toLowerCase() ?? "",
          )),
    ) &&
    result.cancelled_count +
      result.already_cancelled_count +
      result.failed_count ===
      ids.length &&
    result.results.filter((row) => !row.success).length === result.failed_count
  );
}

export function timeOffCancellationOutcome(
  operation: TimeOffCancellationOperation,
  result: BulkCancelResult,
): ActionOutcome {
  const ids = operation.appointmentIds;
  const malformed = !confirmsCancellationResult(result, ids);
  const rows = malformed ? [] : result.results;
  const uncertain = malformed;
  const failed = rows.filter((row) => !row.success);
  const review = rows.filter((row) => row.credit_review_required);
  const excluded = operation.applied.schedule.conflicting_appointments.filter(
    (row) => !ids.includes(row.appointment_id),
  );
  const conflictById = new Map(
    operation.applied.schedule.conflicting_appointments.map((row) => [
      row.appointment_id,
      row,
    ]),
  );
  return {
    variant: uncertain
      ? "uncertain"
      : failed.length || review.length
        ? "attention"
        : "success",
    title: uncertain
      ? "Time blocked; cancellation outcome needs checking"
      : failed.length || review.length
        ? "Time blocked; cancellations need attention"
        : "Time blocked and lessons cancelled",
    description: `${operation.applied.block.block_date} to ${operation.applied.block.end_date} · ${operation.applied.block.start_time === null ? "all day" : `${operation.applied.block.start_time.slice(0, 5)}–${operation.applied.block.end_time?.slice(0, 5)}`}`,
    details: [
      { value: "Time off saved. New bookings are blocked." },
      { label: "Block reason", value: operation.applied.block.reason },
      {
        value: malformed
          ? "The cancellation response did not confirm every selected lesson."
          : `${result.cancelled_count} ${result.cancelled_count === 1 ? "lesson" : "lessons"} cancelled; ${result.already_cancelled_count} already cancelled; ${result.failed_count} selected lessons not confirmed cancelled.`,
      },
      { label: "Cancellation reason", value: operation.reason },
      {
        label: "Cancellation emails requested",
        value: operation.notify ? "On" : "Off",
      },
      ...(!malformed
        ? [
            {
              value: `${result.credits_restored} lesson credits returned; ${result.credits_granted ?? 0} rebooking credits issued.`,
            },
          ]
        : []),
      ...(failed.length
        ? [
            {
              value: (
                <>
                  <strong>Lessons needing attention</strong>
                  <ul>
                    {failed.map((row) => (
                      <li key={row.appointment_id}>
                        {conflictById.has(row.appointment_id)
                          ? conflictDescription(
                              conflictById.get(row.appointment_id)!,
                            )
                          : `Lesson #${row.appointment_id}`}{" "}
                        — {row.message || "Cancellation not confirmed"}
                        {row.final_status
                          ? ` (current status: ${row.final_status})`
                          : ""}
                      </li>
                    ))}
                  </ul>
                </>
              ),
            },
          ]
        : []),
      ...(review.length
        ? [
            {
              value: (
                <>
                  <strong>Credit/payment review required</strong>
                  <ul>
                    {review.map((row) => (
                      <li key={row.appointment_id}>
                        Lesson #{row.appointment_id} —{" "}
                        {row.message || "Check the Action Centre"}
                      </li>
                    ))}
                  </ul>
                </>
              ),
            },
          ]
        : []),
      ...(excluded.length
        ? [
            {
              value: (
                <>
                  <strong>{excluded.length} lessons excluded</strong>
                  <ul>
                    {excluded.map((row) => (
                      <li key={row.appointment_id}>
                        {conflictDescription(row)}
                      </li>
                    ))}
                  </ul>
                </>
              ),
            },
          ]
        : []),
      ...(uncertain
        ? [
            {
              value: `Check the existing cancellation before another write. Operation identity: ${operation.key}`,
            },
          ]
        : []),
      { value: "Recurring plans remain unchanged." },
    ],
  };
}
